import { Modal } from 'antd';
import { LOCATION_INTEL_COPY } from '../copy';
import type { MapColours } from '../mapSpec';
import { Legend } from './Legend';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LOCATION-INTEL.1] THE MAP KEY — the side panel, moved into a dialog so the map takes the whole area.
 *
 *  The user's instruction. Everything the panel held is here, unchanged: the counts in words, when the page read
 *  them, the truncation notice if any, the legend and its two notes. Nothing is reworded on its way in.
 *
 *  ⚠️ THE COUNTS ARE STILL READ BY A SCREEN READER WITHOUT OPENING THIS: the page keeps the same sentence as
 *  visually hidden text beside the map, because a canvas says nothing at all.
 *
 *  ⚠️ A CONTROLLED `Modal`, never `modal.info` ([P7C.1 §3]: that holder renders above the router and the query
 *  client). ONE census site, EXEMPT in `e2e/fixtures/overlays.ts` — the e2e world grants nobody the flag.
 *
 *  ⚠️ ITS BODY'S HEIGHT CAP IS IN VIEWPORT UNITS, AND THAT IS CORRECT ONLY BECAUSE IT IS A DIALOG: a `Modal` is
 *  portalled to `document.body`, outside `<main>`, so the viewport is what it must measure ([C1e §2], [C3b §5]).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = LOCATION_INTEL_COPY;

export const MapKey = ({
  open,
  onClose,
  colours,
  summary,
  truncated,
  updated,
}: {
  open: boolean;
  onClose: () => void;
  colours: MapColours;
  /** The counts in words, or the loading sentence while the points are read. */
  summary: string;
  /** The shortfall notice — the page bound's, or a list that moved mid-read — `null` when every officer was drawn. */
  truncated: string | null;
  /**
   * When the page read the points, that the last refresh failed, or the server's sentence for a refused one —
   * `null` before the first read.
   */
  updated: string | null;
}) => (
  <Modal open={open} title={copy.mapKeyTitle} footer={null} width={520} centered destroyOnHidden onCancel={onClose}>
    <div className="flex max-h-[65vh] flex-col gap-4 overflow-y-auto pr-1 text-sm" data-testid="location-intel-map-key">
      <div className="flex flex-col gap-1" data-testid="location-intel-summary">
        <p>{summary}</p>
        {truncated !== null && (
          <p className="font-medium" data-testid="location-intel-truncated">
            {truncated}
          </p>
        )}
        {updated !== null && (
          <p className="text-muted-foreground" data-testid="location-intel-updated">
            {updated}
          </p>
        )}
      </div>
      <Legend colours={colours} />
    </div>
  </Modal>
);
